// src/components/resources/tools/generators/shared/useExport.ts

import { useCallback, useEffect, useRef, useState } from 'react'

import { warmExportPipeline } from './exportImage'

export interface UseExportOptions {
  /**
   * Preload the heavy `html2canvas` chunk on mount. Defaults to `true`. Set to
   * `false` for exporters that don't use html2canvas (e.g. ChartMockup
   * serializes its SVG natively / rasterizes via `Image`+canvas), so they don't
   * pull an unused chunk on idle while still getting the re-entrancy guard and
   * busy flag.
   */
  warm?: boolean
}

export interface UseExportResult {
  /**
   * True only while the heavy capture is actually running — flip it on via the
   * `beginCapture` callback `runExport` hands to your closure, so the
   * disabled/"Exporting…" affordance tracks the rasterization rather than any
   * preceding `waitForStable` wait (and never shows on an abort path).
   */
  isExporting: boolean
  /**
   * Run a generator's async export with the shared lifecycle.
   *
   * - **Re-entrancy guard (synchronous):** a second click in the same frame is
   *   dropped. A state read can't dedupe these — `isExporting` isn't committed
   *   before the handler's first `await` — so a ref does it synchronously.
   * - **`beginCapture`:** call it from inside `run` once the heavy capture is
   *   actually starting (typically right after a `waitForStable` gate) to flip
   *   `isExporting` true.
   * - **Always resets:** `isExporting` and the guard are cleared in a `finally`,
   *   including the early-return / error / abort paths.
   */
  runExport: (run: (beginCapture: () => void) => Promise<void>) => Promise<void>
}

/**
 * Shared export lifecycle for the generator tools. Owns the three things every
 * exporter needs identically:
 *
 * 1. **Warms `html2canvas` on mount** (idle-scheduled via `warmExportPipeline`)
 *    so the first Download doesn't pay the cold dynamic-import + module-eval on
 *    the click's critical path — the measured dominant export-INP cost.
 * 2. **A synchronous re-entrancy guard** so rapid double-clicks fire one export.
 * 3. **An `isExporting` flag** for the button's loading affordance.
 *
 * The *variable* part — `waitForStable` gating, the `captureAndDownload` args,
 * analytics, single- vs dual-format — stays in the `run` closure each generator
 * passes, so one hook fits every exporter without constraining its specifics.
 */
export function useExport({ warm = true }: UseExportOptions = {}): UseExportResult {
  const [isExporting, setIsExporting] = useState(false)
  const exportingRef = useRef(false)

  useEffect(() => {
    if (!warm) return
    // Returns a cleanup that cancels a still-pending warm on unmount.
    return warmExportPipeline()
  }, [warm])

  const runExport = useCallback(
    async (run: (beginCapture: () => void) => Promise<void>): Promise<void> => {
      if (exportingRef.current) return
      exportingRef.current = true
      try {
        await run(() => setIsExporting(true))
      } finally {
        exportingRef.current = false
        setIsExporting(false)
      }
    },
    [],
  )

  return { isExporting, runExport }
}
